class CartDrawer extends HTMLElement {
  constructor() {
    super();
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.get('viewcart') === 'true') this.open();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', this.onBodyClick.bind(this));
    this.setupCheckoutValidation();
  }

  setupCheckoutValidation() {
    this.addEventListener('click', async (e) => {
      const button = e.target.closest('#CartDrawer-Checkout');
      if (button && button.getAttribute('name') === 'checkout') {
        e.preventDefault();
        e.stopPropagation();
        
        const limitsExceeded = await this.checkCartLimits();
        
        if (!limitsExceeded) {
          window.location.href = '/checkout';
        }
      }
    }, true);
    
    this.addEventListener('submit', async (e) => {
      const form = e.target;
      if (form && form.id === 'CartDrawer-Form') {
        const submitter = e.submitter;
        if (submitter && submitter.getAttribute('name') === 'checkout') {
          e.preventDefault();
          e.stopPropagation();
          
          const limitsExceeded = await this.checkCartLimits();
          
          if (!limitsExceeded) {
            window.location.href = '/checkout';
          }
        }
      }
    }, true);
  }

  async checkCartLimits() {
    const cartLimits = document.querySelector('.cart-limits');
    if (!cartLimits) return false;

    const amountLimit = parseFloat(cartLimits.dataset.amount);
    const quantityLimit = parseInt(cartLimits.dataset.quantity);

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const currentAmount = cart.total_price / 100;
      const currentQuantity = cart.item_count;

      const amountExceeded = amountLimit > 0 && currentAmount > amountLimit;
      const quantityExceeded = quantityLimit > 0 && currentQuantity > quantityLimit;

      if (amountExceeded || quantityExceeded) {
        this.showLimitPopup(amountExceeded);
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  showLimitPopup(byPrice = true) {
    const limitPopup = this.querySelector('.limit-popup');
    if (limitPopup) {
      limitPopup.setAttribute('aria-hidden', 'false');
      if (byPrice) {
        limitPopup.classList.add('amount-limit-reached');
      } else {
        limitPopup.classList.remove('amount-limit-reached');
      }
    }
  }

  onBodyClick(e) {
    if (e.target.classList.contains("limit-popup-close")) {
      e.preventDefault();
      this.closeLimitPopup();
    }
  }

  closeLimitPopup() {    
    const limitPopup = this.querySelector(".limit-popup")
    if (limitPopup) limitPopup.setAttribute('aria-hidden', 'true')
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        
        document.dispatchEvent(new CustomEvent('cartDrawerOpen'));
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const newHtml = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      
      if(newHtml) sectionElement.innerHTML = newHtml;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const newElement = new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
    return newElement ? newElement.innerHTML : false;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        selector: '#cart-icon-bubble'
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#cart-icon-bubble',
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
